import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/common";
import { Saved, StepShell } from "./StepShell";

export const OutletStep = ({ t, canEdit, onSaved }) => {
  const done = !!t.outlet_call_date;
  const closed = ["Resolved", "Closed"].includes(t.status);
  const [editing, setEditing] = useState(!done);
  const [form, setForm] = useState({ summary: "", action: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({ summary: t.outlet_manager_summary || "", action: t.outlet_manager_action || "" });
    setEditing(!t.outlet_call_date);
  }, [t.id, t.outlet_call_date, t.outlet_manager_summary, t.outlet_manager_action]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/followups/${t.id}/outlet`, form);
      toast.success("Outlet call saved");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const edit = canEdit && !closed && done && !editing && (
    <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="outlet-step-edit">Edit</Button>
  );
  return (
    <StepShell n={1} testid="step-outlet" state={done ? "done" : "active"} title="Call the outlet manager"
      hint={done ? `Called on ${fmtDate(t.outlet_call_date)}` : "Find out what happened and what the outlet did about it."} right={edit}>
      {editing && canEdit && !closed ? (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="om-summary">What did the outlet manager say?</Label>
            <Textarea id="om-summary" data-testid="outlet-summary-input" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="e.g. Rush hour, the order was packed before the gravy was ready" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="om-action">Action taken by the outlet</Label>
            <Textarea id="om-action" data-testid="outlet-action-input" rows={2} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} placeholder="e.g. Briefed the kitchen team, added a packing check" />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy || !form.summary.trim()} className="rounded-xl" data-testid="outlet-step-save">{busy && <Spinner className="mr-2 h-4 w-4" />}Save outlet call</Button>
            {done && <Button variant="ghost" onClick={() => setEditing(false)} data-testid="outlet-step-cancel">Cancel</Button>}
          </div>
        </div>
      ) : done ? (
        <Saved items={[["Outlet manager said", t.outlet_manager_summary], ["Action by outlet", t.outlet_manager_action]]} />
      ) : null}
    </StepShell>
  );
};
